import {
  CalendarClock,
  CheckCircle,
  Undo2,
  UserPlus,
} from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React from "react";
import { Pressable } from "react-native";
import { XStack, YStack } from "tamagui";
import { getDayJs, getDayJsDate, isTimeStampInThePast } from "../../core/dayJs";
import {
  HelperType,
  type HelpItem,
  HelpItemRequiredType,
} from "../../helper/HelperManager";
import { useHelper } from "../../helper/useHelper";
import { Box } from "../../ui/Box";
import { Button } from "../../ui/Button";
import { Checkbox } from "../../ui/Checkbox";
import { ModuleEditor } from "../../ui/editor/useModuleEditor";
import { Group } from "../../ui/Group";
import { InfoRow } from "../../ui/InfoRow";
import { Label } from "../../ui/Label";
import { Pill, PillType } from "../../ui/Pill";
import { Sheet } from "../../ui/Sheet";
import { Subtext } from "../../ui/Subtext";
import { TipsText } from "../../ui/TipsText";
import { Title } from "../../ui/Title";
import { UserUI } from "../../users/UserUI";
import { DateStartDateEndModal } from "../shared/DateStartDateEndModal";
import type DateDueModuleModel from "./DateDueModuleModel";

export const DateDueUI = observer(
  ({ module }: { module: DateDueModuleModel }) => {
    const [showUserSheet, setShowUserSheet] = React.useState(false);

    const isCompleted =
      module.completedStatus.count === module.completedStatus.total;
    const showPills = !isCompleted && (module.isOngoing || module.isDue);

    const items: HelpItem[] = React.useMemo(
      () => [
        {
          type: HelpItemRequiredType.Required,
          label: "Add a description below",
          isCompleted: () => {
            return module.hasDescription;
          },
        },
        {
          type: HelpItemRequiredType.Optional,
          label: "Set a start and optional end date",
          isCompleted: () => {
            return !!module.data.dateStart;
          },
        },

        {
          type: HelpItemRequiredType.Tip,
          label: "You can assign specific users to complete this event",
          isEnabled: () => {
            return !module.data.assignedTo.length;
          },
          isCompleted: () => {
            return false;
          },
        },

        {
          type: HelpItemRequiredType.Tip,
          label: "Register your email to get reminders",
          isEnabled: () => {
            return !module.plan.userInPlan?.data.email;
          },
          isCompleted: () => {
            return false;
          },
        },
      ],
      [
        module.hasDescription,
        module.data.dateStart,
        module.plan.userInPlan?.data.email,
        module.data.assignedTo,
      ],
    );

    const helper = useHelper(module.uid, HelperType.DateDue, items);

    return (
      <YStack width="100%" gap="$space.medium">
        {helper}

        <Box>
          <YStack
            flex={1}
            width="100%"
            marginTop="$space.small"
            gap={"$space.large"}
          >
            {showPills && (
              <XStack gap="$medium" alignItems="center">
                {module.isOngoing && <Pill type={PillType.Info}>ONGOING</Pill>}
                {module.isDue && <Pill type={PillType.Danger}>OVER DUE</Pill>}
              </XStack>
            )}

            <XStack
              width="100%"
              justifyContent="space-between"
              alignItems="center"
            >
              <Label>Information</Label>
              <XStack justifyContent="flex-end" gap={"$space.small"}>
                <Pressable
                  onPress={() => {
                    module.toggleFormSheet();
                  }}
                >
                  <CalendarClock size={"$size.normal"} />
                </Pressable>

                <Pressable
                  onPress={() => {
                    setShowUserSheet((v) => !v);
                  }}
                >
                  <UserPlus size={"$size.normal"} />
                </Pressable>
              </XStack>
            </XStack>

            <ModuleEditor module={module} />

            <XStack flex={1} width="100%" gap={"$space.small"}>
              <YStack gap={"$space.small"} flex={1}>
                {module.data.dateStart && (
                  <XStack flex={1} gap={"$space.small"} alignItems="center">
                    <Label>
                      {isTimeStampInThePast(module.data.dateStart)
                        ? "Due"
                        : "Starts"}{" "}
                      {getDayJs(module.data.dateStart)}
                    </Label>
                    <Subtext>{getDayJsDate(module.data.dateStart)}</Subtext>
                  </XStack>
                )}

                {module.infoRowItems && <InfoRow items={module.infoRowItems} />}
              </YStack>
            </XStack>

            {module.isCompletedByUser && (
              <TipsText text="You have set this as completed" />
            )}

            {!module.userCanComplete && !module.isCompletedByUser && (
              <TipsText text="You can only complete this when you are assigned to it" />
            )}
          </YStack>
        </Box>

        <Button
          type={module.isCompletedByUser ? "secondary" : "primary"}
          disabled={!module.userCanComplete}
          icon={module.isCompletedByUser ? Undo2 : CheckCircle}
          onPress={() => {
            module.toggleCompleted();
          }}
        >
          {module.isCompletedByUser ? "Change back status" : "Set as finished"}
        </Button>

        {module.showFormSheet && (
          <DateStartDateEndModal
            initialDateStart={module.data.dateStart}
            initialDateEnd={module.data.dateEnd}
            visible={module.showFormSheet}
            module={module}
            onSave={async ({ dateStart, dateEnd }) => {
              void module.updateDates(dateStart, dateEnd);
            }}
            onClose={() => module.toggleFormSheet()}
          />
        )}

        <Sheet
          sheetId="date_assign_users"
          open={showUserSheet}
          onOpenChange={setShowUserSheet}
          header={<Title>Assign users</Title>}
        >
          <Group borderTop={false}>
            {module.plan.users.map((user) => {
              const isAssigned = module.data.assignedTo.includes(user.uid);
              return (
                <XStack
                  key={user.uid}
                  cursor="pointer"
                  width="100%"
                  onPress={() => {
                    module.toggleAssignee(user.uid);
                  }}
                  gap={"$medium"}
                  alignItems="center"
                  flex={1}
                >
                  <Checkbox checked={isAssigned} />
                  <UserUI uid={user.uid} />
                </XStack>
              );
            })}
          </Group>
        </Sheet>
      </YStack>
    );
  },
);
